import type { ChatAttachmentUpload } from '@newfront-insurance/ai-api';
import { Button, cn } from '@newfront-insurance/core-ui/v2';
import { File, X } from 'lucide-react';
import { useRef, type Dispatch, type SetStateAction } from 'react';

import { AttachmentsProvider } from './attachments-provider';
import { ChatAttachmentActions } from './chat-attachment-actions';
import { ChatAttachmentsPreview } from './chat-attachments-preview';
import { ChatInputFrame } from './chat-input';
import { ChatInputProvider } from './chat-input-context';
import { ChatInputTextarea } from './chat-input-textarea';
import { ChatInputToolbar } from './chat-input-toolbar';
import { DragOverlayView } from './drag-overlay';
import type { PromptEditorHandle } from './prompt-editor';
import { useFileSelection, type FileAttachment } from '../../../../hooks/use-file-selection';
import { readyUploads } from '../../../../lib/chat-workspace';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';

/** A file a prompt (a schedule's) attaches: in the owner's workspace, or a legacy Files record. */
export interface NovaPromptFile {
  path?: string;
  /** LEGACY: a Files record attached before sandbox workspaces. */
  uuid?: string;
  filename: string;
}

function promptFileKey(file: NovaPromptFile): string {
  return file.path ?? file.uuid ?? file.filename;
}

interface NovaPromptEditorProps {
  value: string;
  attachments: FileAttachment[];
  existingFiles: NovaPromptFile[];
  accountUuid?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  onChange: (value: string) => void;
  setAttachments: Dispatch<SetStateAction<FileAttachment[]>>;
  onExistingFilesChange: (files: NovaPromptFile[]) => void;
  /** The chat whose workspace new files upload into; the one on screen by default. */
  chatUuid?: string;
}

export function NovaPromptEditor({
  value,
  attachments,
  existingFiles,
  accountUuid,
  disabled = false,
  placeholder = 'Describe what Nova should do...',
  className,
  onChange,
  setAttachments,
  onExistingFilesChange,
  chatUuid,
}: NovaPromptEditorProps) {
  return (
    <AttachmentsProvider attachments={attachments} setAttachments={setAttachments}>
      <NovaPromptEditorContent
        value={value}
        existingFiles={existingFiles}
        accountUuid={accountUuid}
        disabled={disabled}
        placeholder={placeholder}
        className={className}
        onChange={onChange}
        setAttachments={setAttachments}
        onExistingFilesChange={onExistingFilesChange}
        chatUuid={chatUuid}
      />
    </AttachmentsProvider>
  );
}

function NovaPromptEditorContent({
  value,
  existingFiles,
  accountUuid,
  disabled = false,
  placeholder,
  className,
  onChange,
  setAttachments,
  onExistingFilesChange,
  chatUuid: uploadChatUuid,
}: Omit<NovaPromptEditorProps, 'attachments'>) {
  const { chatUuid } = useChatOverlayContext();
  const editorRef = useRef<HTMLTextAreaElement | HTMLDivElement>(null);
  const promptEditorRef = useRef<PromptEditorHandle>(null);
  const {
    fileInputRef,
    isDragging,
    handleFileUpload,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePaste,
    handleAttachmentClick,
    handleFolderAttachmentClick,
    supportedFileTypes,
  } = useFileSelection({ setAttachments, chatUuid: uploadChatUuid ?? chatUuid });
  return (
    <ChatInputProvider
      value={{
        input: value,
        setInput: onChange,
        editorRef,
        promptEditorRef,
        handleSubmit: () => undefined,
        status: 'ready',
        isAtBottom: true,
        scrollToBottom: () => undefined,
      }}
    >
      <ChatInputFrame
        className={className}
        isDragging={isDragging}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <DragOverlayView isDragging={isDragging} />
        <ChatInputTextarea
          placeholder={placeholder}
          ariaLabel="Schedule prompt"
          disabled={disabled}
          skillsAccountUuid={accountUuid}
          preferSkillsContext={false}
          submitOnEnter={false}
          onPasteFiles={handlePaste}
          className="min-h-[100px]"
        />
        <ChatInputToolbar className="border-steel-75 gap-1 border-t">
          <ChatAttachmentActions
            className="shrink-0"
            disabled={disabled}
            fileInputRef={fileInputRef}
            supportedFileTypes={supportedFileTypes}
            onAttachmentClick={handleAttachmentClick}
            onFileUpload={handleFileUpload}
            onFolderAttachmentClick={() => void handleFolderAttachmentClick()}
          />
          <ExistingPromptFiles files={existingFiles} disabled={disabled} onChange={onExistingFilesChange} />
          <ChatAttachmentsPreview />
        </ChatInputToolbar>
      </ChatInputFrame>
    </ChatInputProvider>
  );
}

function ExistingPromptFiles({
  files,
  disabled,
  onChange,
}: {
  files: NovaPromptFile[];
  disabled: boolean;
  onChange: (files: NovaPromptFile[]) => void;
}) {
  if (files.length === 0) return null;

  return (
    <div className="flex min-w-0 items-center gap-1 overflow-x-auto overflow-y-hidden">
      {files.map((file) => (
        <div
          key={promptFileKey(file)}
          className="border-steel-200 text-steel-500 inline-flex h-8 max-w-[180px] shrink-0 items-center gap-1.5 rounded-[30px] border bg-white px-2.5 py-1.5"
        >
          <File aria-hidden="true" className="text-steel-400 size-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-[12px] leading-4">{file.filename}</span>
          <Button
            type="button"
            variant="transparent"
            size="small"
            aria-label={`Remove ${file.filename}`}
            disabled={disabled}
            onClick={() => onChange(files.filter((candidate) => promptFileKey(candidate) !== promptFileKey(file)))}
            className={cn(
              'text-steel-400 hover:text-steel-500 h-fit w-fit shrink-0 rounded-full border-none bg-transparent p-0.5',
              'hover:bg-steel-100',
            )}
          >
            <X aria-hidden="true" className="size-3" />
          </Button>
        </div>
      ))}
    </div>
  );
}

export function mergeNovaPromptFiles(existingFiles: NovaPromptFile[], attachments: FileAttachment[]): NovaPromptFile[] {
  const files = new Map(existingFiles.map((file) => [promptFileKey(file), file]));
  readyUploads(attachments.filter((attachment) => !attachment.isUploading)).forEach(({ path, name }) => {
    files.set(path, { path, filename: name });
  });
  return [...files.values()];
}

/** A schedule's files: its inputs, and any legacy Files attachments. */
export function scheduleNovaPromptFiles(schedule: {
  attachments?: ReadonlyArray<{ path: string; name: string }> | null;
  /** LEGACY(schedule-fileUuids): delete once ai-legacy-schedule-attachments has run. */
  attachmentFiles?: ReadonlyArray<{ uuid: string; filename: string }> | null;
}): NovaPromptFile[] {
  return [
    ...(schedule.attachments ?? []).map(({ path, name }) => ({ path, filename: name })),
    ...(schedule.attachmentFiles ?? []).map(({ uuid, filename }) => ({ uuid, filename })),
  ];
}

/** Files in the workspace at these paths (a proposal's), named after the path. */
export function novaPromptFilesAt(paths: readonly string[] | null | undefined): NovaPromptFile[] {
  return (paths ?? []).map((path) => ({ path, filename: path.slice(path.lastIndexOf('/') + 1) }));
}

/**
 * What a schedule save sends for these files: the workspace files it keeps, and the uploads still
 * among them (committed on save). Legacy Files attachments can't be kept once the files change.
 */
export function novaPromptFileAttachments(
  files: readonly NovaPromptFile[],
  uploads: readonly ChatAttachmentUpload[],
): { attachmentPaths: string[]; attachmentUploads: ChatAttachmentUpload[] } {
  const uploadPaths = new Set(uploads.map(({ path }) => path));
  const paths = files.flatMap(({ path }) => (path ? [path] : []));
  return {
    attachmentPaths: paths.filter((path) => !uploadPaths.has(path)),
    attachmentUploads: uploads.filter(({ path }) => paths.includes(path)),
  };
}

export function hasUnsettledNovaPromptAttachments(attachments: FileAttachment[]): boolean {
  return attachments.some((attachment) => attachment.isUploading || Boolean(attachment.uploadError));
}
